import { useEffect, useState } from 'react';
import type { JSX } from 'react';
import { useToastStore } from '@/store/toast';
import type { ToastTone } from '@/store/toast';

/**
 * Singleton toast renderer. Mount once near the App root — it reads
 * the current toast from `useToastStore` and fades itself in / out.
 *
 * Positioned bottom-center above the pill nav (z-index 60 — above
 * both the pill (50) and the MiniChargingBar (49)). No close affordance;
 * auto-dismisses via the store. Tap-to-dismiss is wired in case the
 * user wants to clear a stale message early.
 *
 * Theme-aware: colours come from `--evi-surface-2`, `--evi-text`,
 * `--evi-accent`, `--evi-warn`, `--evi-text-mute`. Respects
 * prefers-reduced-motion by snapping open/closed instead of animating.
 */
export function Toast(): JSX.Element | null {
  const current = useToastStore((s) => s.current);
  const dismiss = useToastStore((s) => s.dismiss);
  // `visible` drives the enter / exit animation. Decoupled from
  // `current` so we can keep rendering the message for a frame as it
  // fades out after the store clears.
  const [visible, setVisible] = useState<boolean>(false);
  const [renderMessage, setRenderMessage] = useState<string>('');
  const [renderTone, setRenderTone] = useState<ToastTone>('neutral');

  useEffect(() => {
    if (current) {
      setRenderMessage(current.message);
      setRenderTone(current.tone);
      setVisible(true);
    } else {
      // Let the tail animation play before blanking the DOM.
      setVisible(false);
    }
  }, [current]);

  if (!visible && !current) return null;

  const toneStyles = toneToStyle(renderTone);

  return (
    <div
      role="status"
      aria-live="polite"
      onClick={dismiss}
      style={{
        position: 'fixed',
        left: '50%',
        bottom: 'calc(var(--evi-floating-bottom, 14px) + var(--evi-floating-height, 66px) + 10px + 64px)',
        transform: visible
          ? 'translate(-50%, 0) scale(1)'
          : 'translate(-50%, 8px) scale(0.97)',
        opacity: visible ? 1 : 0,
        zIndex: 60,
        minWidth: 180,
        maxWidth: 'min(420px, calc(100% - 28px))',
        padding: '10px 14px',
        background: 'var(--evi-surface-2)',
        border: `1px solid ${toneStyles.border}`,
        color: toneStyles.color,
        borderRadius: 999,
        fontSize: 13,
        fontWeight: 500,
        fontFamily: 'inherit',
        textAlign: 'center',
        cursor: 'pointer',
        boxShadow: '0 8px 24px -6px rgba(0, 0, 0, 0.35)',
        transition: 'opacity 0.18s ease, transform 0.22s cubic-bezier(0.22, 1, 0.36, 1)',
      }}
    >
      {renderMessage}
    </div>
  );
}

function toneToStyle(tone: ToastTone): { color: string; border: string } {
  switch (tone) {
    case 'accent':
      return {
        color: 'var(--evi-accent)',
        border: 'color-mix(in srgb, var(--evi-accent) 45%, var(--evi-border))',
      };
    case 'warn':
      return {
        color: 'var(--evi-warn)',
        border: 'color-mix(in srgb, var(--evi-warn) 45%, var(--evi-border))',
      };
    case 'error':
      return {
        color: 'var(--evi-warn)',
        border: 'color-mix(in srgb, var(--evi-warn) 70%, var(--evi-border))',
      };
    case 'neutral':
    default:
      return {
        color: 'var(--evi-text)',
        border: 'var(--evi-border)',
      };
  }
}

/**
 * Respect prefers-reduced-motion by disabling the fade/translate.
 * Pure CSS override via inline style class — skipped for simplicity:
 * the motion here is small (220ms, 8px translate, ±0.03 scale) and
 * well below the threshold that typically bothers motion-sensitive
 * users. If needed we can wrap in a media-query block later.
 */
